/**
 * <s-collection-search> — in-page product search for the collection sidebar.
 *
 * Typing in its input (debounced), pressing Enter / the search button, or clicking any
 * [data-collection-search-term] link inside the layout fetches
 *   /search?q=…&type=product&options[prefix]=last&view=grid   (templates/search.grid.liquid)
 * and shows [data-search-results] in the layout's [data-collection-search-panel], hiding the
 * collection grid ([data-collection-results]). Clearing restores the collection grid.
 * The term is mirrored to ?q= in the address bar so reloads and shared links keep it.
 *
 * It stops its own input/change events so the surrounding live filter form does not submit,
 * and resets itself when that form re-renders the collection grid (s-live-form:updated).
 */

// Brings the collection toolbar and first product row into view after a sidebar action,
// only when the buyer has scrolled past them; respects the sticky header's height.
function scrollToCollectionResults() {
  const target = document.querySelector('[data-collection-layout]');
  if (!target) return;
  const header = document.querySelector('header');
  const headerOffset = header && getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().height : 0;
  const top = target.getBoundingClientRect().top - headerOffset - 16;
  if (top >= 0) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollBy({ top, behavior: reduce ? 'auto' : 'smooth' });
}
const DEBOUNCE_MS = 350;

class CollectionSearch extends HTMLElement {
  #abort = null;
  #request = null;
  #timer = null;
  #layout = null;
  #panel = null;
  #body = null;
  #summary = null;
  #input = null;
  #term = '';

  connectedCallback() {
    this.#layout = document.querySelector(this.dataset.layout || '[data-collection-layout]');
    this.#input = this.querySelector('[data-collection-search-input]');
    this.#panel = this.#layout?.querySelector('[data-collection-search-panel]');
    this.#body = this.#panel?.querySelector('[data-search-body]');
    this.#summary = this.#panel?.querySelector('[data-search-summary]');
    if (!this.#layout || !this.#input || !this.#panel || !this.#body) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const stop = (event) => event.stopPropagation();

    this.addEventListener('change', stop, { signal });
    this.addEventListener(
      'input',
      (event) => {
        event.stopPropagation();
        clearTimeout(this.#timer);
        const terms = this.#input.value.trim();
        if (!terms) {
          this.clear();
          return;
        }
        this.#timer = setTimeout(() => this.search(terms), DEBOUNCE_MS);
      },
      { signal },
    );
    this.#input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          clearTimeout(this.#timer);
          this.search(this.#input.value.trim());
        } else if (event.key === 'Escape' && this.#input.value) {
          event.preventDefault();
          this.clear();
        }
      },
      { signal },
    );
    this.querySelector('[data-collection-search-submit]')?.addEventListener(
      'click',
      () => {
        clearTimeout(this.#timer);
        this.search(this.#input.value.trim());
      },
      { signal },
    );

    this.#layout.addEventListener(
      'click',
      (event) => {
        const termLink = event.target.closest('[data-collection-search-term]');
        if (termLink && this.#layout.contains(termLink)) {
          event.preventDefault();
          const terms = termLink.dataset.collectionSearchTerm;
          this.#input.value = terms;
          this.search(terms);
          return;
        }
        const clearButton = event.target.closest('[data-search-clear]');
        if (clearButton) {
          this.clear();
          if (this.contains(clearButton)) this.#input.focus();
        }
      },
      { signal },
    );

    // A filter/sort change re-renders the collection grid: leave search mode.
    document.addEventListener(
      's-live-form:updated',
      (event) => {
        // Only the collection's own filter form (not the header search drawer).
        if (!(event.target instanceof Element) || !event.target.closest('s-live-form[data-collection-id]')) return;
        if (this.#term) this.clear({ keepGrid: true });
        scrollToCollectionResults();
      },
      { signal },
    );

    const initial = new URL(window.location.href).searchParams.get('q');
    if (initial) {
      this.#input.value = initial;
      this.search(initial.trim());
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  async search(terms) {
    if (!terms) {
      this.#input.focus();
      return;
    }
    if (terms === this.#term && !this.#panel.hidden) return;
    this.#term = terms;
    this.#request?.abort();
    this.#request = new AbortController();

    this.#showPanel();
    scrollToCollectionResults();
    this.#markTerms(terms);
    this.#syncUrl(terms);
    this.#setSummary(this.dataset.loadingText || '');
    this.#body.dataset.loading = '';

    const url = new URL(this.dataset.url || '/search', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('view', 'grid');

    try {
      const response = await fetch(url, { signal: this.#request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = doc.querySelector('[data-search-results]');
      if (!results) throw new Error('Search view unavailable');
      if (terms !== this.#term) return;
      this.#body.replaceChildren(...[...results.childNodes].map((node) => document.importNode(node, true)));
      const count = results.dataset.count || '0';
      this.#setSummary(
        (this.dataset.summary || '__COUNT__ “__TERMS__”').replace('__COUNT__', count).replace('__TERMS__', terms),
      );
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#body.replaceChildren();
      this.#setSummary(this.dataset.errorText || '');
    } finally {
      if (terms === this.#term) delete this.#body.dataset.loading;
    }
  }

  clear({ keepGrid = false } = {}) {
    clearTimeout(this.#timer);
    this.#request?.abort();
    this.#term = '';
    this.#input.value = '';
    this.#markTerms('');
    this.#syncUrl('');
    this.#panel.hidden = true;
    this.#body.replaceChildren();
    delete this.#body.dataset.loading;
    // After a filter re-render the fresh grid is already visible; otherwise un-hide the old one.
    if (!keepGrid) this.#toggleCollection(true);
  }

  #showPanel() {
    this.#toggleCollection(false);
    this.#panel.hidden = false;
  }

  #toggleCollection(visible) {
    for (const node of this.#layout.querySelectorAll('[data-collection-results]')) node.hidden = !visible;
  }

  #setSummary(text) {
    if (this.#summary) this.#summary.textContent = text;
  }

  #markTerms(terms) {
    const wanted = terms.toLowerCase();
    for (const link of this.#layout.querySelectorAll('[data-collection-search-term]')) {
      const active = wanted !== '' && link.dataset.collectionSearchTerm.toLowerCase() === wanted;
      link.toggleAttribute('data-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  }

  #syncUrl(terms) {
    const url = new URL(window.location.href);
    if (terms) url.searchParams.set('q', terms);
    else url.searchParams.delete('q');
    history.replaceState(history.state, '', url);
  }
}

if (!customElements.get('s-collection-search')) customElements.define('s-collection-search', CollectionSearch);
